import TenantService from '@/services/tenant.service';
import CustomizationService from '@/services/customization.service';
import { applyTenantColor, applyTenantSecondaryColor } from '@/utils/tenantTheme';

const state = {
  tenant: null,
  carregando: false,
  erro: null,
};

const getters = {
  tenantAtual:     (state) => state.tenant,
  tenantSlug:      (state) => state.tenant?.slug || null,
  tenantNome:      (state) => state.tenant?.nome || 'SalesFlow',
  tenantConfig:    (state) => state.tenant?.config || {},
  corPrimaria:     (state) => state.tenant?.config?.cor_primaria   || '#1976D2',
  corSecundaria:   (state) => state.tenant?.config?.cor_secundaria || '#0ea5e9',
  logoUrl:         (state) => state.tenant?.config?.logo_url       || null,
  tenantCarregando:(state) => state.carregando,
};

const mutations = {
  SET_TENANT(state, tenant) {
    state.tenant = tenant;
  },
  SET_CARREGANDO(state, valor) {
    state.carregando = valor;
  },
  SET_ERRO(state, erro) {
    state.erro = erro;
  },
  LIMPAR_TENANT(state) {
    state.tenant = null;
    state.erro = null;
  },
  SET_COR_PRIMARIA(state, cor) {
    if (!state.tenant) return;
    if (!state.tenant.config) state.tenant.config = {};
    state.tenant.config.cor_primaria = cor;
  },
  SET_COR_SECUNDARIA(state, cor) {
    if (!state.tenant) return;
    if (!state.tenant.config) state.tenant.config = {};
    state.tenant.config.cor_secundaria = cor;
  },
  SET_LOGO_URL(state, url) {
    if (!state.tenant) return;
    if (!state.tenant.config) state.tenant.config = {};
    state.tenant.config.logo_url = url;
  },
  SET_NOME(state, nome) {
    if (!state.tenant) return;
    state.tenant.nome = nome;
  },
};

const actions = {
  async carregarTenant({ commit }) {
    commit('SET_CARREGANDO', true);
    commit('SET_ERRO', null);
    try {
      const tenant = await TenantService.getInfo();
      commit('SET_TENANT', tenant);

      // Limpa valores do tenant anterior antes de aplicar o novo (evita vazamento cross-tenant)
      localStorage.removeItem('tenant_cor_primaria');
      localStorage.removeItem('tenant_cor_secundaria');
      localStorage.removeItem('tenant_logo_url');

      // Salva o slug do tenant cujos dados foram cacheados
      if (tenant?.slug) {
        localStorage.setItem('tenant_cached_slug', tenant.slug);
      }

      // Persiste customização no localStorage para evitar flicker no reload
      if (tenant?.config?.cor_primaria) {
        localStorage.setItem('tenant_cor_primaria', tenant.config.cor_primaria);
        applyTenantColor(tenant.config.cor_primaria);
      } else {
        applyTenantColor('#1976D2');
      }
      if (tenant?.config?.cor_secundaria) {
        localStorage.setItem('tenant_cor_secundaria', tenant.config.cor_secundaria);
        applyTenantSecondaryColor(tenant.config.cor_secundaria);
      } else {
        applyTenantSecondaryColor('#0ea5e9');
      }
      if (tenant?.config?.logo_url) {
        localStorage.setItem('tenant_logo_url', tenant.config.logo_url);
      }
      return tenant;
    } catch (err) {
      commit('SET_ERRO', err.message || 'Erro ao carregar tenant');
      console.error('[TenantStore] Erro ao carregar tenant:', err.message);
      return null;
    } finally {
      commit('SET_CARREGANDO', false);
    }
  },

  async salvarCorPrimaria({ commit }, cor) {
    const result = await CustomizationService.update({ cor_primaria: cor });
    const impersonating = !!localStorage.getItem('dkpImpersonateSlug') &&
                          localStorage.getItem('dkpImpersonateSlug') !== 'dkp';
    if (!impersonating) {
      commit('SET_COR_PRIMARIA', result.cor_primaria);
      localStorage.setItem('tenant_cor_primaria', result.cor_primaria);
      applyTenantColor(result.cor_primaria);
    }
    return result;
  },

  async salvarCorSecundaria({ commit }, cor) {
    const result = await CustomizationService.update({ cor_secundaria: cor });
    const impersonating = !!localStorage.getItem('dkpImpersonateSlug') &&
                          localStorage.getItem('dkpImpersonateSlug') !== 'dkp';
    if (!impersonating) {
      commit('SET_COR_SECUNDARIA', result.cor_secundaria);
      localStorage.setItem('tenant_cor_secundaria', result.cor_secundaria);
      applyTenantSecondaryColor(result.cor_secundaria);
    }
    return result;
  },

  async salvarLogoUrl({ commit }, url) {
    const result = await CustomizationService.update({ logo_url: url });
    const impersonating = !!localStorage.getItem('dkpImpersonateSlug') &&
                          localStorage.getItem('dkpImpersonateSlug') !== 'dkp';
    if (!impersonating) {
      commit('SET_LOGO_URL', result.logo_url);
      if (result.logo_url) {
        localStorage.setItem('tenant_logo_url', result.logo_url);
      } else {
        localStorage.removeItem('tenant_logo_url');
      }
    }
    return result;
  },

  async salvarNome({ commit }, nome) {
    const result = await CustomizationService.update({ nome });
    const impersonating = !!localStorage.getItem('dkpImpersonateSlug') &&
                          localStorage.getItem('dkpImpersonateSlug') !== 'dkp';
    if (!impersonating) {
      commit('SET_NOME', result.nome || nome);
    }
    return result;
  },

  async uploadLogo({ commit }, { file, onProgress }) {
    const result = await CustomizationService.uploadLogo(file, onProgress);
    const impersonating = !!localStorage.getItem('dkpImpersonateSlug') &&
                          localStorage.getItem('dkpImpersonateSlug') !== 'dkp';
    if (!impersonating) {
      commit('SET_LOGO_URL', result.logo_url);
      if (result.logo_url) {
        localStorage.setItem('tenant_logo_url', result.logo_url);
      }
    }
    return result;
  },

  limparTenant({ commit }) {
    commit('LIMPAR_TENANT');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
